import { Autocomplete, Button, Flex, Stack } from '@mantine/core';
import { Organization } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';

type AddRelatedPartnerFormProps = {
  onAdd: (partner: Organization) => void;
  onClose: () => void;
  excludedOrganizationIds: string[];
};

export function AddRelatedPartnerForm({ onAdd, onClose, excludedOrganizationIds }: AddRelatedPartnerFormProps) {
  const medplum = useMedplum();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 300);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<Organization | null>(null);

  // Fetch organizations based on search term, excluding already-added partners
  useEffect(() => {
    async function fetchOrganizations() {
      try {
        const searchParams: Record<string, any> = {
          _count: 50,
        };

        // Add name search if there's a search term
        if (debouncedSearchTerm) {
          searchParams.name = debouncedSearchTerm;
        }

        // Exclude already-added organizations
        if (excludedOrganizationIds.length > 0) {
          searchParams['_id:not'] = excludedOrganizationIds.join(',');
        }

        const results = await medplum.searchResources('Organization', searchParams);
        setOrganizations(results as Organization[]);
      } catch {
        setOrganizations([]);
      }
    }

    fetchOrganizations();
  }, [medplum, debouncedSearchTerm, excludedOrganizationIds]);

  const handleAdd = async () => {
    if (!selectedPartner) return;
    await onAdd(selectedPartner);
    setSelectedPartner(null);
    setSearchTerm('');
    onClose();
  };

  const handleSelect = (value: string) => {
    const org = organizations.find((o) => o.id === value || o.name === value);
    if (org) {
      setSelectedPartner(org);
    }
  };

  return (
    <Stack gap={8} mb={8}>
      <div onMouseDown={(e) => e.stopPropagation()}>
        <Autocomplete
          placeholder="Search partner"
          value={searchTerm}
          onChange={setSearchTerm}
          onOptionSubmit={handleSelect}
          data={organizations.map((org) => ({
            value: org.id || '',
            label: org.name || 'Unknown Organization',
          }))}
          radius="sm"
          size="sm"
        />
      </div>

      <Flex justify="flex-end" gap={8}>
        <Button variant="subtle" color="blue" radius="sm" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button color="blue" radius="lg" size="sm" onClick={handleAdd} disabled={!selectedPartner}>
          Save
        </Button>
      </Flex>
    </Stack>
  );
}
